// The relay's message edges this client holds: fed live by cyborg:message_edge_changed and, when a session
// opens, by cyborg:fetch_message_edges. The peer rows read it; a relay that has no edges (an older one)
// leaves it empty and every row keeps reading its tool result.
import { SvelteMap } from "svelte/reactivity";
import { client } from "$lib/state/client.js";
import { workspaceState } from "$lib/core/state.svelte.js";
import { reportUiFailure } from "$lib/report-failure.js";
import {
  edgeOfReceivedRow,
  newerEdge,
  parseMessageEdge,
  type MessageEdge,
} from "./message-edge.js";

// ponytail: one flat map for the page, oldest dropped past the cap. Upgrade path: a bucket per session.
const MAX_EDGES = 2000;
const edges = new SvelteMap<string, MessageEdge>();
// agentId -> the connection generation its edges were last read on (one read per open, again after a reconnect).
const loaded = new Map<string, number>();
// $state: the stream's load effect reads it, so a reconnect re-reads the open session's edges.
let generation = $state(0);
// The relay answered "unsupported": it has no ledger, so nothing asks again until the next connection.
let unsupported = false;

function put(raw: unknown): void {
  const edge = parseMessageEdge(raw);
  if (!edge) return;
  const kept = newerEdge(edges.get(edge.edgeId), edge);
  if (kept !== edges.get(edge.edgeId)) edges.set(edge.edgeId, kept);
  if (edges.size <= MAX_EDGES) return;
  const oldest = [...edges.values()].sort((a, b) => a.sentAt - b.sentAt)[0];
  edges.delete(oldest.edgeId);
}

/** Every edge held, oldest first. Reactive. */
export const messageEdges = (): MessageEdge[] => [...edges.values()];

/** One edge by its id (a relayed chat row's `handoffId`), or undefined when this client does not hold it. Reactive. */
export const messageEdgeById = (edgeId: string): MessageEdge | undefined => edges.get(edgeId);

/** The ids the viewed session sends and receives as: its own id and, when it is an agent's session, the agent's. */
export function edgeSelfIds(agentId: string): ReadonlySet<string> {
  const cyboId = workspaceState.agents.find((a) => a.agentId === agentId)?.cyboId;
  return new Set(cyboId ? [agentId, cyboId] : [agentId]);
}

/** The edge of a "From" row in `agentId`'s stream (a hand-off it received), or undefined. */
export function receivedEdgeOf(
  agentId: string,
  row: { chainId?: string; fromSessionId?: string } | undefined,
  at?: number,
): MessageEdge | undefined {
  if (!row?.chainId) return undefined;
  return edgeOfReceivedRow({ ...row, at }, messageEdges(), edgeSelfIds(agentId));
}

/** Read a node's edges again now (a person's forward that just got its answer). */
export function reloadMessageEdges(agentId: string): Promise<void> {
  loaded.delete(agentId);
  return loadMessageEdges(agentId);
}

/** Live: the relay changed an edge. */
export function noteMessageEdge(p: { edge?: unknown }): void {
  put(p.edge);
}

/** A session's edges, read once per open and again after a reconnect (a change may have been missed). */
export async function loadMessageEdges(agentId: string): Promise<void> {
  const wsId = workspaceState.current?.id;
  const at = generation;
  if (!wsId || unsupported || loaded.get(agentId) === at) return;
  loaded.set(agentId, at);
  try {
    const res = await client.fetchMessageEdges(wsId, agentId);
    for (const raw of Array.isArray(res?.edges) ? res.edges : []) put(raw);
  } catch (err) {
    if (loaded.get(agentId) === at) loaded.delete(agentId);
    // A relay without the ledger: not a failure, the rows fall back to the tool result.
    if (/^unsupported\b/i.test(err instanceof Error ? err.message : "")) unsupported = true;
    else reportUiFailure("message_edges.fetch", err, { agentId });
  }
}

client.on("message_edge_changed", noteMessageEdge);
client.on("connection", ({ status }) => {
  if (status !== "connected") return;
  generation += 1;
  unsupported = false;
});

/** Test-only. */
export function resetMessageEdges(): void {
  edges.clear();
  loaded.clear();
  generation = 0;
  unsupported = false;
}
